import * as LocalBrandConfig from "../../../shared/node/BrandConfig";

import * as Graph from "../../GeneratedGraphQL";

/**
 * The subset of `Brand` fields this function knows how to resolve a
 * per-field cascading fallback for. Accepts partial/`Pick`-shaped brand
 * objects so callers don't need to construct a fully-typed `Graph.Brand`.
 */
export type EffectiveBrandFields = Pick<
  Graph.Brand,
  | "name"
  | "light"
  | "dark"
  | "brandThemeMode"
  | "allowVisitorThemeOverride"
  | "logoUrl"
  | "bannerUrl"
  | "fontUrl"
  | "fontFamily"
  | "faviconUrl"
  | "pageTitleSuffix"
>;

type LegacyMicrositeColours = {
  primary?: Graph.Maybe<string>;
  secondary?: Graph.Maybe<string>;
};

type ResolveEffectiveBrandOptions = {
  /**
   * The BookingTool's own `brand` field. For a real BookingTool, this is
   * already the server-side `brand` field resolver's result — which itself
   * already folds in `customColours` as a fallback when `brand` is unset on
   * the document (see src/BookingTools/Graph/resolvers/brand/brand.ts) — so
   * this function does NOT need to separately consider `customColours`.
   */
  bookingToolBrand?: Graph.Maybe<Partial<EffectiveBrandFields>>;
  /**
   * The organisation-level brand config (Organisation.brandConfig), Phase 1
   * of org-level brand configuration — see
   * src/shared/node/OrganisationBrandConfig.
   */
  orgBrandConfig?: Graph.Maybe<Partial<EffectiveBrandFields>>;
  /**
   * The legacy two-colour microsite colours (Organisation.micrositeColours),
   * the oldest/most limited source of brand colour data.
   */
  legacyMicrositeColours?: Graph.Maybe<LegacyMicrositeColours>;
};

function toBrandThemeMode(
  brandThemeMode: Graph.Maybe<Graph.BrandThemeMode> | undefined,
): "auto" | "light" | "dark" | undefined {
  if (brandThemeMode === Graph.BrandThemeMode.Dark) return "dark";
  if (brandThemeMode === Graph.BrandThemeMode.Light) return "light";
  if (brandThemeMode === Graph.BrandThemeMode.Auto) return "auto";
  return undefined;
}

function fromBrandThemeMode(
  brandThemeMode: "auto" | "light" | "dark" | undefined,
): Graph.BrandThemeMode | undefined {
  if (brandThemeMode === "dark") return Graph.BrandThemeMode.Dark;
  if (brandThemeMode === "light") return Graph.BrandThemeMode.Light;
  if (brandThemeMode === "auto") return Graph.BrandThemeMode.Auto;
  return undefined;
}

function toPalette(
  palette: Graph.Maybe<Graph.BrandPalette> | undefined,
): LocalBrandConfig.BrandPalette | undefined {
  if (!palette) return undefined;
  return {
    primaryColor: palette.primaryColor ?? undefined,
    primaryActionColor: palette.primaryActionColor ?? undefined,
    accentColor: palette.accentColor ?? undefined,
    primaryDarkColor: palette.primaryDarkColor ?? undefined,
    primaryLightColor: palette.primaryLightColor ?? undefined,
    surfaceColor: palette.surfaceColor ?? undefined,
    textColor: palette.textColor ?? undefined,
    pageBackground: palette.pageBackground ?? undefined,
    logoUrl: palette.logoUrl ?? undefined,
    bannerUrl: palette.bannerUrl ?? undefined,
  };
}

function fromPalette(
  palette: LocalBrandConfig.BrandPalette,
): Graph.BrandPalette {
  return {
    __typename: "BrandPalette",
    accentColor: palette.accentColor ?? null,
    bannerUrl: palette.bannerUrl ?? null,
    logoUrl: palette.logoUrl ?? null,
    pageBackground: palette.pageBackground ?? null,
    primaryActionColor: palette.primaryActionColor ?? null,
    primaryColor: palette.primaryColor ?? null,
    primaryDarkColor: palette.primaryDarkColor ?? null,
    primaryLightColor: palette.primaryLightColor ?? null,
    surfaceColor: palette.surfaceColor ?? null,
    textColor: palette.textColor ?? null,
  };
}

function toBrandConfigLayer(
  brand: Graph.Maybe<Partial<EffectiveBrandFields>> | undefined,
): LocalBrandConfig.BrandConfig | undefined {
  if (!brand) return undefined;
  return {
    light: toPalette(brand.light),
    dark: toPalette(brand.dark),
    brandThemeMode: toBrandThemeMode(brand.brandThemeMode),
    allowVisitorThemeOverride: brand.allowVisitorThemeOverride ?? undefined,
    brandFontUrl: brand.fontUrl ?? undefined,
    brandFontFamily: brand.fontFamily ?? undefined,
    logoUrl: brand.logoUrl ?? undefined,
    bannerUrl: brand.bannerUrl ?? undefined,
    faviconUrl: brand.faviconUrl ?? undefined,
    pageTitleSuffix: brand.pageTitleSuffix ?? undefined,
  };
}

/**
 * Converts the legacy two-colour `micrositeColours` into a synthetic,
 * lowest-precedence `BrandConfig` layer, so it can be folded into the same
 * `resolveBrandPalette` calls as the other tiers — mirroring the
 * historical mapping of `primary`/`secondary` onto `light.primaryColor`/
 * `light.accentColor` (legacy `micrositeColours` predates the dark-theme
 * concept, so it can only ever populate a `light` palette). `secondary`
 * also feeds `primaryActionColor`, matching sibling brand-consuming apps'
 * (e.g. micrositesv2's `buildBrandConfig`) treatment of the legacy "second
 * colour" as the primary action/button colour, not merely the accent
 * colour.
 */
function legacyMicrositeColoursToBrandConfig(
  legacyMicrositeColours: Graph.Maybe<LegacyMicrositeColours> | undefined,
): LocalBrandConfig.BrandConfig | undefined {
  if (!legacyMicrositeColours) return undefined;
  return {
    light: {
      primaryColor: legacyMicrositeColours.primary ?? undefined,
      primaryActionColor: legacyMicrositeColours.secondary ?? undefined,
      accentColor: legacyMicrositeColours.secondary ?? undefined,
    },
  };
}

/**
 * Resolves the single "effective" Brand fields to use for rendering, from
 * (in order of ascending precedence) the legacy `micrositeColours`, the
 * organisation-level `brandConfig`, and finally the BookingTool's own
 * `brand`.
 *
 * The `light`/`dark` palettes are each resolved (per-field, per-theme)
 * via `LocalBrandConfig.resolveBrandPalette` (from
 * `@appointedd/zod-types`), which itself falls back to Appointedd's static
 * per-theme default palette for any field left unset by every tier — so
 * the returned `light`/`dark` are always fully populated. Every other
 * field (`name`/`logoUrl`/`bannerUrl`/`brandThemeMode`/
 * `allowVisitorThemeOverride`/fonts) is resolved separately, per-field, in
 * the same ascending precedence order.
 *
 * The result is itself `Graph.Brand`-shaped, so it can be assigned
 * directly to a (possibly synthetic, client-constructed) `BookingTool.brand`
 * field — see GenericBookingToolData.tsx.
 */
export function resolveEffectiveBrand({
  bookingToolBrand,
  orgBrandConfig,
  legacyMicrositeColours,
}: ResolveEffectiveBrandOptions): EffectiveBrandFields {
  const layers = [
    legacyMicrositeColoursToBrandConfig(legacyMicrositeColours),
    toBrandConfigLayer(orgBrandConfig),
    toBrandConfigLayer(bookingToolBrand),
  ];

  const light = LocalBrandConfig.resolveBrandPalette(layers, "light");
  const dark = LocalBrandConfig.resolveBrandPalette(layers, "dark");

  const field = <K extends keyof EffectiveBrandFields>(
    key: K,
  ): EffectiveBrandFields[K] | undefined =>
    bookingToolBrand?.[key] ?? orgBrandConfig?.[key] ?? undefined;

  return {
    allowVisitorThemeOverride: field("allowVisitorThemeOverride") ?? true,
    bannerUrl: field("bannerUrl"),
    brandThemeMode:
      field("brandThemeMode") ?? fromBrandThemeMode("auto") ?? null,
    dark: fromPalette(dark),
    faviconUrl: field("faviconUrl"),
    fontFamily: field("fontFamily"),
    fontUrl: field("fontUrl"),
    light: fromPalette(light),
    logoUrl: field("logoUrl"),
    name: field("name"),
    pageTitleSuffix: field("pageTitleSuffix"),
  };
}
